import { init } from '@socialgouv/matomo-next';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

import { EnvironmentService } from '@/lib/environmentVariables';

const MATOMO_URL = EnvironmentService.getMatomoURL();
const MATOMO_SITE_ID = EnvironmentService.getMatmoSiteId();
const canonical = EnvironmentService.getCanonicalURL();

const pluginConfig: CookieConsentConfig = {
  guiOptions: {
    consentModal: {
      layout: 'bar',
      position: 'bottom',
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      position: 'left',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  onConsent: function ({ cookie }) {
    const analticsCookie = cookie.categories.some(
      (item) => item === 'analytics',
    );

    if (analticsCookie) {
      init({
        url: MATOMO_URL,
        siteId: MATOMO_SITE_ID,
      });
    }
  },

  onChange: ({ cookie }) => {
    const analyticsCookie = cookie.categories.includes('analytics');

    init({
      url: MATOMO_URL,
      siteId: MATOMO_SITE_ID,
      disableCookies: !analyticsCookie,
    });
  },

  categories: {
    necessary: {
      readOnly: true,
      enabled: true,
    },
    analytics: {
      autoClear: {
        cookies: [
          {
            name: /^(_ga|_gid|MATOMO_)/,
          },
        ],
      },
    },
  },

  language: {
    default: 'en',

    translations: {
      en: {
        consentModal: {
          description:
            'We use cookies which are essential for you to access our website and/or to provide you with our services, enable you to share our website content via your social media accounts and allow us to measure and improve the performance of our website.',
          acceptAllBtn: 'Accept all cookies',
          acceptNecessaryBtn: 'Reject all cookies',
          showPreferencesBtn: 'Manage preferences',
          //closeIconLabel: 'Close',
        },
        preferencesModal: {
          title: 'Cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Save preferences',
          closeIconLabel: 'Close',
          sections: [
            {
              title: 'Cookie Usage',
              description: `CERN uses cookies to arrange and display the website optimally in accordance with your preferences (such as login, language, font size and other display preferences) so that you do not have to keep re-entering preferences each time you visit the website. Read our full <a href="${canonical}/page/privacy-policy" class="cc__link" target="_blank">cookie policy</a>.`,
            },
            {
              title: 'Strictly necessary cookies',
              description:
                'These cookies are necessary for the functioning of the website.',
              linkedCategory: 'necessary',
              cookieTable: {
                headers: {
                  name: 'Name',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: 'cookiejsr',
                    description:
                      'Used to record which cookies the web user has consented to.',
                    expiration: '1 year',
                  },
                  {
                    name: 'SimpleSAMLAuthToken, SimpleSAMLSessionID',
                    description:
                      'Used, only when a web user logs in with a username and password, to distinguish authenticated sessions.',
                    expiration: '8 hours validity tokens or until logged out',
                  },
                  {
                    name: 'SSESS...',
                    description:
                      'Used, only when a web user logs in with a username and password, to keep track of the current authenticated session.',
                    expiration: 'End of session',
                  },
                ],
              },
            },
            {
              title: 'Matomo Analytics',
              description:
                'CERN uses Matomo Analytics to analyse traffic to the website and help improve website performance. Embedded videos from the CERN Document Server (CDS) also use Matomo Analytics to track viewing statistics. <br />Matomo Analytics data is stored at CERN.',
              linkedCategory: 'analytics',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_pk_id...',
                    domain: 'Matomo Analytics',
                    description: 'Used to distinguish web users',
                    expiration: '13 months',
                  },
                  {
                    name: '_pk_ses...',
                    domain: 'Matomo Analytics',
                    description: 'Used to keep track of the current session',
                    expiration: '30 minutes',
                  },
                  {
                    name: 'session',
                    domain: 'Matomo Analytics',
                    description:
                      'Used by videos.cern.ch, only when there is an embedded CERN Document Server (CDS) video on a webpage, to track statistics and provide a better user experience e.g. replaying a video from where the user previously stopped watching.',
                    expiration: 'End of session',
                  },
                ],
              },
            },
          ],
        },
      },
    },
  },
};

export default pluginConfig;
